/**
 * How far back the board is looking.
 *
 * This is the control that decides which roles get a row — and with it, whether
 * somebody hired in April reads as this year's hire or as a colleague who was
 * already here. Both are true; which one you want depends on the question, so it
 * is a control and not a rule (see demandWindow.ts).
 *
 * There is deliberately NO "as of" date beside it. An earlier draft had one and
 * Theresa cut it: "today is today. I don't want to go back in time looking at
 * how it looked in April." The window moves what is being looked back OVER, and
 * nothing here changes what counts as now.
 */
import type { DemandWindow } from './demandWindow';
import { TRIMESTERS, trimesterWindow, yearWindow } from './demandWindow';

const btn = (on: boolean) =>
  `rounded-sm px-2 py-0.5 text-[11px] font-semibold transition-colors ${
    on ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
  }`;

export function WindowPicker({
  value,
  onChange,
  roles,
  total,
}: {
  value: DemandWindow;
  onChange: (w: DemandWindow) => void;
  /** Roles the window keeps, and how many there are in all — so narrowing is visible. */
  roles: number;
  total: number;
}) {
  const year = Number(value.from.slice(0, 4));
  const activeT =
    value.kind === 'trimester' ? Math.floor((Number(value.from.slice(5, 7)) - 1) / 4) + 1 : null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className="text-[11px] font-semibold uppercase tracking-wider text-gray-400"
        title="Which roles get a row of their own. Everyone hired outside this span sits in their team's current members instead."
      >
        Looking back over
      </span>
      <div className="flex items-center gap-0.5 rounded-md border border-gray-200 p-0.5">
        <button type="button" className={btn(value.kind === 'year')} onClick={() => onChange(yearWindow(year))}>
          {year}
        </button>
        {TRIMESTERS.map((t) => (
          <button
            key={t}
            type="button"
            className={btn(activeT === t)}
            onClick={() => onChange(trimesterWindow(year, t))}
            title={`T${t} ${year} — roles starting in those four months`}
          >
            T{t}
          </button>
        ))}
      </div>
      {roles < total && (
        <span className="text-[11px] text-gray-500">
          {roles} of {total} roles
          <span className="text-gray-400"> · the rest show as current team members</span>
        </span>
      )}
    </div>
  );
}
